import { ClipboardCopy, Clock, Hash, Link2, Loader2, Plus, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { trpc } from "../../lib/trpc";
import { Button, Input } from "../ui";

type Props = {
  clubId: string;
  canManageInvites: boolean;
};

export function InvitationsSection({ clubId, canManageInvites }: Props) {
  return (
    <div className="space-y-6">
      <InviteLinksPanel clubId={clubId} canManageInvites={canManageInvites} />
      <DirectInvitePanel clubId={clubId} />
    </div>
  );
}

function InviteLinksPanel({
  clubId,
  canManageInvites,
}: {
  clubId: string;
  canManageInvites: boolean;
}) {
  const utils = trpc.useUtils();
  const { data: links, isLoading } = trpc.clubInvite.listLinks.useQuery({ clubId });
  const [showCreate, setShowCreate] = useState(false);
  const [maxUses, setMaxUses] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");

  const createMutation = trpc.clubInvite.createLink.useMutation({
    onSuccess: () => {
      toast.success("Invite link created");
      void utils.clubInvite.listLinks.invalidate({ clubId });
      setShowCreate(false);
      setMaxUses("");
      setExpiresInDays("");
    },
    onError: (err) => toast.error(err.message || "Failed to create link"),
  });

  const revokeMutation = trpc.clubInvite.revokeLink.useMutation({
    onSuccess: () => {
      toast.success("Invite link revoked");
      void utils.clubInvite.listLinks.invalidate({ clubId });
    },
    onError: () => toast.error("Failed to revoke link"),
  });

  const handleCreate = () => {
    const opts: { maxUses?: number; expiresAt?: string } = {};
    if (maxUses) opts.maxUses = parseInt(maxUses, 10);
    if (expiresInDays) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(expiresInDays, 10));
      opts.expiresAt = d.toISOString();
    }
    createMutation.mutate({ clubId, ...opts });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-surface-400 uppercase tracking-wider">
          Invite Links
        </label>
        {canManageInvites && (
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus className="size-3.5" />}
            onClick={() => setShowCreate(!showCreate)}
          >
            {showCreate ? "Cancel" : "Create Link"}
          </Button>
        )}
      </div>

      {showCreate && (
        <div className="space-y-3 p-4 rounded-lg bg-surface-900/50 border border-surface-700/40">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Max Uses (optional)"
              placeholder="Unlimited"
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
            />
            <Input
              label="Expires in (days)"
              placeholder="Never"
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={createMutation.isPending}
            leftIcon={
              createMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Link2 className="size-3.5" />
              )
            }
            onClick={handleCreate}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-surface-700/30 animate-pulse" />
          ))}
        </div>
      ) : links && links.length > 0 ? (
        <div className="space-y-2">
          {links.map((link) => {
            const joinUrl = `${window.location.origin}/app/clubs/join/${link.code}`;
            const isExpired = link.expiresAt && new Date(link.expiresAt) < new Date();
            const isUsedUp = link.maxUses !== null && link.useCount >= link.maxUses;

            return (
              <div
                key={link.id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                  isExpired || isUsedUp
                    ? "bg-surface-900/30 border-surface-700 opacity-60"
                    : "bg-surface-900/50 border-surface-700/40"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <code className="text-xs text-brand-400 font-mono truncate block">
                    {link.code}
                  </code>
                  <div className="flex items-center gap-3 mt-1 text-2xs text-surface-500">
                    {link.maxUses !== null && (
                      <span className="flex items-center gap-1">
                        <Hash className="size-3" />
                        {link.useCount}/{link.maxUses} uses
                      </span>
                    )}
                    {link.expiresAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="size-3" />
                        {isExpired
                          ? "Expired"
                          : `Expires ${new Date(link.expiresAt).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2"
                  onClick={() => {
                    void navigator.clipboard.writeText(joinUrl);
                    toast.success("Link copied");
                  }}
                >
                  <ClipboardCopy className="size-3.5" />
                </Button>
                {canManageInvites && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!px-2 !text-error-400 hover:!bg-error-500/10"
                    disabled={revokeMutation.isPending}
                    onClick={() => revokeMutation.mutate({ clubId, linkId: link.id })}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-surface-500 py-2">No invite links created yet</p>
      )}
    </div>
  );
}

function DirectInvitePanel({ clubId }: { clubId: string }) {
  const [userId, setUserId] = useState("");

  const sendMutation = trpc.clubInvite.send.useMutation({
    onSuccess: () => {
      toast.success("Invite sent!");
      setUserId("");
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT") {
        toast.error("User already has a pending invite or is a member");
      } else if (err.data?.code === "NOT_FOUND") {
        toast.error("User not found");
      } else {
        toast.error(err.message || "Failed to send invite");
      }
    },
  });

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim()) return;
    sendMutation.mutate({ clubId, inviteeId: userId.trim() });
  };

  return (
    <div className="space-y-3">
      <label className="text-xs font-medium text-surface-400 uppercase tracking-wider">
        Direct Invite
      </label>
      <form onSubmit={handleSend} className="flex gap-3">
        <Input
          placeholder="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="flex-1"
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!userId.trim() || sendMutation.isPending}
          leftIcon={
            sendMutation.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )
          }
        >
          Send
        </Button>
      </form>
      <p className="text-xs text-surface-500">
        Enter a user ID to send them a direct invite to the club.
      </p>
    </div>
  );
}
